import { CardRenderer } from '@littlebird/action-cards-view/react';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
  reportedSurfaceOf,
  trackCardShown,
  type WebSurface,
} from './analytics';
import { cardFrame, isFeedSurface, isHomePreview } from './cardFrame';
import { CardOpenContext, CardSaveEditsContext } from './cardOpen';
import { registerCardEditsSaver } from './cardPendingEdits';
import { hasViewFor } from './cardRow';
import { HostStatusActionContext, webRegistry } from './registry';
import { withRunEvents, type RunEvents } from './runEvents';
import { useCardHandlers } from './useCardHandlers';

import type {
  ActionCardSurface,
  DeferredDismissState,
} from '@littlebird/action-cards';
import type { CardDismissal } from '@littlebird/action-cards-view';
import type { ActionCard } from '@littlebird/sdk';

export type CardHeaderRenderer = (
  title?: string,
  actions?: ReactNode,
  // The body shows the card's reason in full itself, so the header leaves out its shortened copy
  bodyShowsReason?: boolean
) => ReactNode;

export type CardV2Props = RunEvents & {
  card: ActionCard;
  surface: WebSurface;
  // The surface's own dismissal, so its feedback strip and events stay as they are
  deferredDismiss: DeferredDismissState;
  // A host that reports its own surface today (canvas, a routine report) keeps it
  reportedSurface?: ActionCardSurface;
  // Off where the host's old card never reported the card shown there
  reportsShown?: boolean;
  header?: ReactNode | CardHeaderRenderer;
  body?: ReactNode | ((header: CardHeaderRenderer) => ReactNode);
  // Opens the card's own chat in place, sending the seed to the agent when one is given
  openCardChat?: (seed?: string) => void;
  openPlainCardChat?: () => void;
  cardExpanded?: boolean;
};

export const CardV2 = observer(function CardV2({
  card,
  surface,
  deferredDismiss,
  reportedSurface,
  onRun,
  onRunSettled,
  onRunEnd,
  reportsShown = true,
  header,
  body,
  openCardChat,
  openPlainCardChat,
  cardExpanded = false,
}: CardV2Props) {
  // React Compiler would cache `card.row` on the stable MobX card and freeze it after the first edit
  'use no memo';
  const hasView = hasViewFor(card, surface);
  const shown = body !== undefined || hasView;
  const resolvedSurface = reportedSurface ?? reportedSurfaceOf(surface);
  // The revision the server gave the latest save of this card's edits. A save call below clears it before it waits
  // and reads it after, so only the save it waited on can leave a value for it
  const savedRevision = useRef<number | null>(null);
  const { handlers: cardHandlers, onFieldsCommit } = useCardHandlers(
    card,
    surface,
    {
      deferredDismiss,
      reportedSurface,
      openCardChat,
      openPlainCardChat,
      cardExpanded,
      onEditSaved: (savedSyncId) => {
        savedRevision.current = savedSyncId;
      },
    }
  );
  // Set by the press itself, so a card already dismissed when shown is not announced
  const [dismissedHere, setDismissedHere] = useState(false);
  // The renderer's save of the card's unsaved edits, which the host's links on the card await too
  const saveEditsRef = useRef<(() => Promise<boolean>) | null>(null);
  const saveEdits = () => saveEditsRef.current?.() ?? Promise.resolve(true);
  useEffect(
    () =>
      registerCardEditsSaver(card.id, async () => {
        savedRevision.current = null;
        const saved = (await saveEditsRef.current?.()) ?? true;
        const revision = savedRevision.current;
        savedRevision.current = null;
        return { saved, revision };
      }),
    [card.id]
  );
  const handlers = {
    ...withRunEvents(cardHandlers, { onRun, onRunSettled, onRunEnd }),
    dismiss: (dismissal?: CardDismissal) => {
      setDismissedHere(true);
      return cardHandlers.dismiss(dismissal);
    },
  };
  const restore = cardHandlers.restore;
  // A dismissed chat card stays in place, folded to its row with Restore beside the status
  const folded =
    card.status === 'dismissed' &&
    !isHomePreview(surface) &&
    restore !== undefined &&
    hasViewFor(card, 'web_row');

  useEffect(() => {
    if (shown && reportsShown) trackCardShown(card, resolvedSurface, 'full');
  }, [card, resolvedSurface, shown, reportsShown]);

  const renderHeader = typeof header === 'function' ? header : () => header;
  if (body !== undefined) {
    const content =
      typeof body === 'function' ? (
        body(renderHeader)
      ) : (
        <>
          {renderHeader()}
          {body}
        </>
      );
    return isFeedSurface(surface) ? (
      <div className={cardFrame(surface)} data-testid="card-v2-frame">
        {content}
      </div>
    ) : (
      <>{content}</>
    );
  }
  if (!shown) return null;
  const renderer = (rendered: WebSurface | 'web_row') => (
    <CardOpenContext.Provider value={handlers.open}>
      <CardRenderer
        row={card.row}
        surface={rendered}
        registry={webRegistry.registry}
        handlers={handlers}
        onFieldsCommit={onFieldsCommit}
        saveEditsRef={saveEditsRef}
      />
    </CardOpenContext.Provider>
  );
  return (
    <>
      {folded ? (
        <div className={cardFrame('web_row')} data-testid="card-v2-fold">
          <HostStatusActionContext.Provider
            value={{
              label: 'Restore',
              run: () => void restore(),
              focus: dismissedHere,
            }}
          >
            {renderer('web_row')}
          </HostStatusActionContext.Provider>
        </div>
      ) : (
        <div className={cardFrame(surface)} data-testid="card-v2-frame">
          <CardSaveEditsContext.Provider value={saveEdits}>
            {renderHeader()}
            {renderer(surface)}
          </CardSaveEditsContext.Provider>
        </div>
      )}
      <span className="sr-only" aria-live="polite">
        {folded && dismissedHere ? 'Discarded' : ''}
      </span>
    </>
  );
});
